import type {
  ProductDto,
  WorkspaceMemberWithUserDto,
} from '@up2stay/up2promo-api-client';
import {
  getPhysicalFormLabel,
  resolveBehaviorProductType,
  resolvePhysicalForm,
  resolveProductCardAssignment,
} from '@up2stay/up2promo-commons/products';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Card,
  CardContent,
  cn,
  CopyButton,
  ghostAccentIconButtonClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Link2, MapPin } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { toast } from 'sonner';

import { resourceInteractiveCardClass } from '@/core/components/layout';
import {
  workspaceMemberAvatarUrl,
  workspaceMemberDisplayName,
  workspaceMemberInitials,
} from '@/core/lib/workspace-member-display';

import { PhysicalFormTile } from './physical-form-icons';
import { ProductBehaviorChip } from './product-behavior-chip';
import { ProductScanSparkline } from './product-scan-sparkline';

export type ProductScanSeriesPoint = {
  date: string;
  count: number;
};

export type ProductListCardProps = {
  product: ProductDto;
  productKindLabel: string;
  formLabel: string;
  locationName?: string;
  assigneeMember?: WorkspaceMemberWithUserDto | null;
  noLocationLabel?: string;
  noProfileLabel?: string;
  noTargetLabel?: string;
  scans30dLabel?: string;
  scans30d?: number;
  scanSeries?: ProductScanSeriesPoint[];
  copyHashLabel?: string;
  copyHashSuccess?: string;
  highlighted?: boolean;
  onClick: () => void;
};

export function ProductListCard({
  product,
  productKindLabel,
  formLabel,
  locationName,
  assigneeMember,
  noLocationLabel,
  noProfileLabel,
  noTargetLabel,
  scans30dLabel,
  scans30d,
  scanSeries,
  copyHashLabel,
  copyHashSuccess,
  highlighted = false,
  onClick,
}: ProductListCardProps) {
  const title = product.label?.trim() || product.hash || '—';
  const showScans = scans30dLabel != null && scans30d != null;
  const form = resolvePhysicalForm(product);
  const behaviorType = resolveBehaviorProductType(product);
  const resolvedFormLabel = formLabel || getPhysicalFormLabel(form, undefined);
  const assignment = resolveProductCardAssignment(product);

  const assigneeName = assigneeMember
    ? workspaceMemberDisplayName(assigneeMember)
    : null;
  const assigneeAvatar = assigneeMember
    ? workspaceMemberAvatarUrl(assigneeMember)
    : null;
  const assigneeInitials = assigneeMember
    ? workspaceMemberInitials(assigneeMember)
    : '?';

  let assignmentLabel = '';
  let assignmentAria = '';
  switch (assignment.kind) {
    case 'profile':
      assignmentLabel = assigneeName || assignment.userId;
      assignmentAria = assignmentLabel;
      break;
    case 'location':
      assignmentLabel =
        locationName ?? assignment.locationId ?? noLocationLabel ?? '—';
      assignmentAria = assignmentLabel;
      break;
    case 'target':
      assignmentLabel = assignment.label;
      assignmentAria = assignmentLabel;
      break;
    case 'empty':
      assignmentLabel =
        assignment.reason === 'profile'
          ? (noProfileLabel ?? 'No profile assigned')
          : assignment.reason === 'location'
            ? (noLocationLabel ?? 'No location')
            : (noTargetLabel ?? 'No destination');
      assignmentAria = assignmentLabel;
      break;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      data-product-id={product.id}
      role="button"
      tabIndex={0}
      aria-label={`${title}, ${resolvedFormLabel}, ${productKindLabel}, ${assignmentAria}`}
      className={cn(
        'h-full cursor-pointer',
        resourceInteractiveCardClass,
        highlighted &&
          'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] ring-2 ring-[color:var(--brand-500)]/35 ring-offset-2 ring-offset-background animate-pulse motion-reduce:animate-none',
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <CardContent className="flex h-full flex-col gap-3 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <PhysicalFormTile form={form} label={resolvedFormLabel} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="truncate text-[15.5px] font-bold leading-tight text-foreground">
                {title}
              </span>
              <ProductBehaviorChip
                productType={behaviorType}
                label={productKindLabel}
              />
            </div>

            <div className="flex min-w-0 items-center gap-1.5 text-[13.5px] leading-snug text-foreground">
              {assignment.kind === 'profile' ? (
                <>
                  <Avatar className="size-5 shrink-0">
                    {assigneeAvatar ? (
                      <AvatarImage src={assigneeAvatar} alt="" />
                    ) : null}
                    <AvatarFallback className="text-[9px] font-semibold">
                      {assigneeInitials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 truncate font-medium">
                    {assignmentLabel}
                  </span>
                </>
              ) : null}

              {assignment.kind === 'location' ? (
                <>
                  <MapPin
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 truncate font-medium">
                    {assignmentLabel}
                  </span>
                </>
              ) : null}

              {assignment.kind === 'target' ? (
                <>
                  <Link2
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 truncate font-medium">
                    {assignmentLabel}
                  </span>
                </>
              ) : null}

              {assignment.kind === 'empty' ? (
                <span className="min-w-0 truncate text-muted-foreground">
                  {assignmentLabel}
                </span>
              ) : null}
            </div>
          </div>
          {product.hash ? (
            <CopyButton
              content={product.hash}
              variant="outline"
              size="default"
              className={cn('size-8 shrink-0', ghostAccentIconButtonClass)}
              aria-label={copyHashLabel ?? 'Copy product code'}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onCopy={() => {
                if (copyHashSuccess) toast.success(copyHashSuccess);
              }}
            />
          ) : null}
        </div>

        <div className="mt-auto flex items-end justify-between gap-3">
          {scanSeries && scanSeries.length > 0 ? (
            <ProductScanSparkline
              series={scanSeries}
              className="min-w-0 flex-1"
            />
          ) : (
            <span className="min-w-0 flex-1" aria-hidden />
          )}
          {showScans ? (
            <div className="flex shrink-0 flex-col items-end gap-1 text-right">
              <span
                className={cn(
                  'text-xl font-bold tabular-nums leading-none',
                  scans30d > 0
                    ? 'text-foreground'
                    : 'font-semibold text-muted-foreground',
                )}
              >
                {scans30d}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {scans30dLabel}
              </span>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** Grid-shaped placeholder matching ProductListCard layout. */
export function ProductListCardSkeleton() {
  return (
    <Card className="h-full" aria-hidden>
      <CardContent className="flex h-full flex-col gap-3 p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <Skeleton className="size-11 shrink-0 rounded-xl" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-32 max-w-[55%]" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <div className="flex items-center gap-1.5">
              <Skeleton className="size-5 shrink-0 rounded-full" />
              <Skeleton className="h-3.5 w-28" />
            </div>
          </div>
          <Skeleton className="size-8 shrink-0 rounded-md" />
        </div>
        <div className="mt-auto flex items-end justify-between gap-3">
          <Skeleton className="h-8 w-full max-w-[9rem] rounded-md" />
          <div className="flex flex-col items-end gap-1.5">
            <Skeleton className="h-6 w-10" />
            <Skeleton className="h-2.5 w-12" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
